/**
 * `/` — TASK 10.2 Step 2. REVISED: the library is STATIC (nothing is ever
 * "recently added"), so the mock's FRESH PAINT recently-added rail is gone.
 * Home is now two recently-played rails, both wired to the caller's own history:
 *
 *   HERO RAIL  — recently-played ALBUMS as big cover cards. Sourced from
 *   `GET /api/home` `recentlyPlayed` (TASK 4.4): its ALBUM contexts are
 *   projected to `AlbumCard`s ({@link recentlyPlayedAlbums}) and playlist
 *   contexts dropped (a cover rail shows albums). The API already filters this
 *   section to albums that HAVE cover art, so the rail never shows a bare
 *   gradient tile. `recentlyAdded` is still in the DTO but ignored here.
 *
 *   ON REPEAT  — recently-played TRACKS, fed by its OWN endpoint
 *   (`GET /api/recently-played-tracks`): the last 20 distinct qualifyingly-
 *   played tracks. Rows are full-row play buttons (the FavoritesView idiom)
 *   carrying the stats rail's yellow play count.
 *
 * ── INDEPENDENT STATES (AC 14.3) ──────────────────────────────────────────
 * The two rails load, error and retry INDEPENDENTLY: each keeps its own loader,
 * generation guard, skeleton, inline empty strip and inline error strip with a
 * Retry. One rail failing never blanks the other and never forces a whole-page
 * error (§10.6); a Retry re-issues only its own endpoint.
 *
 * ── AVAILABILITY (AC 14.2 / AC 3.9) ───────────────────────────────────────
 * Markers ride the shared decision function (components/AvailabilityBadge.js):
 * an `available:false` album KEEPS its rail position and renders dimmed with
 * the ⊘ ribbon — never filtered — and its link lands on the RETAINED album
 * page (TASK 10.1 renders a gone album with the 410 banner), never a 404.
 * Offline, a not-downloaded album takes the ⌁ ribbon.
 *
 * @module
 */

import { TESTIDS, playTrackButton } from '@streamymusic/shared';

import {
  AVAILABILITY_COPY,
  AvailabilityBadge,
  availabilityOpacity,
  availabilityState,
  isPlayable,
} from '../../components/AvailabilityBadge.js';
import { albumThumb } from '../../components/CoverThumb.js';
import { SkeletonList } from '../../components/primitives/Skeleton.js';
import { append, el } from '../../components/primitives/dom.js';
import { linkTestId } from '../../app/testids.js';
import { AlbumGridCard } from '../browse/ArtistDetail.js';
import { playsLabel } from '../stats/StatsView.js';

/** mock L194 — the Recently Played empty strip (ui-contract §10.6 verbatim). */
export const HOME_RECENTLY_PLAYED_EMPTY_MESSAGE =
  'No plays yet — hit Browse and drop the needle on something.';
/** The ON REPEAT rail's empty strip — same voice as its sibling above. */
export const HOME_ON_REPEAT_EMPTY_MESSAGE =
  'No track plays yet — the songs you spin will pile up here.';
/** mock L159 / L191 — the per-section inline error strips. */
export const HOME_RECENTLY_PLAYED_ERROR_MESSAGE = "Couldn't load Recently Played.";
export const HOME_ON_REPEAT_ERROR_MESSAGE = "Couldn't load On Repeat.";
/** mock L162 — the admin-only bridge's accessible name (→ `view-sync-status-link`). */
export const HOME_SYNC_BRIDGE_LABEL = 'View sync status →';

/**
 * @typedef {object} HomeHandlers
 * @property {(album: import('@streamymusic/shared').AlbumCard) => void} onAlbumMenu
 *   a Recently-Added card's "⋯" (ui-contract §3 pattern `btn:Actions for *`)
 * @property {(track: import('@streamymusic/shared').TrackCard, playable: import('@streamymusic/shared').TrackCard[]) => void} onPlayTrack
 *   an ON REPEAT row tap — the FavoritesView row idiom: the view hands the
 *   tapped track plus the rail's PLAYABLE slice out, and the composition root
 *   owns the transport (a standalone play — the rail is none of the five
 *   dto.ts play-context types, and standalone plays are exactly what this
 *   list exists to include)
 * @property {(message: string, context: { state: import('../../components/AvailabilityBadge.js').AvailabilityState, trackId: string }) => void} onBlocked
 *   a non-playable ON REPEAT tap (§10.6 toast copy) — same seam as the other
 *   track surfaces, so the §11 offline SLI hears about refused offline taps
 */

/**
 * Builds the `/` section and starts loading both rails.
 *
 * @param {{
 *   api: Pick<import('../../../api/api-client.js').ApiClient, 'getHome'|'getRecentlyPlayedTracks'>,
 *   heading: HTMLElement,
 *   handlers: HomeHandlers,
 *   isAdmin?: boolean,
 *   offline?: boolean,
 *   downloadedAlbums?: readonly string[],
 *   getState?: () => import('../../app/state-store.js').AppState,
 *   announce?: (text: string) => void,
 *   warn?: (message: string, detail: Record<string, string>) => void,
 * }} options
 *   `heading` is the route's `<h1 data-testid="page-heading">` (the shell
 *   contract's one-per-route h1). `isAdmin` gates the empty-library sync
 *   bridge — role comes from the session the composition root already holds,
 *   so the view never re-derives it. `getState` is optional and read only at
 *   paint time, to mark ♥ on rail cards the user has favorited.
 * @returns {{ element: HTMLElement, dispose: () => void }}
 */
export function HomeView(options) {
  const {
    api,
    heading,
    handlers,
    isAdmin = false,
    offline = false,
    downloadedAlbums = [],
    getState,
    announce,
    warn = (message) => {
      console.warn(message);
    },
  } = options;

  const section = el('section', { attrs: { 'data-screen-label': 'Home' } });

  /* ── skeleton, built once — only the two bodies swap ──────────────────── */

  const raBody = el('div');
  const onRepeatTitle = el('h2', {
    className: 'sm-section-title sm-section-title--26 sm-section-title--pink',
    text: 'ON REPEAT',
  });
  const onRepeatBody = el('div');

  append(
    section,
    heading,
    // The library is static — nothing is ever "recently added" — so the hero
    // rail shows recently PLAYED albums (as big cover cards) instead. The old
    // "Back on the Decks" list was the same recently-played data and is folded
    // into this one rail. ON REPEAT (recently played TRACKS) stays below.
    el('p', { className: 'sm-subhead', text: 'Recently played albums' }),
    raBody,
    onRepeatTitle,
    el('p', { className: 'sm-subhead', text: 'Recently played tracks' }),
    onRepeatBody,
  );

  const downloads = new Set(downloadedAlbums);

  /* ── lifecycle guards — one PER SECTION (they retry independently) ────── */

  let disposed = false;
  let raGeneration = 0;
  let onRepeatGeneration = 0;

  /* ── shared section chrome ────────────────────────────────────────────── */

  /**
   * The mock's per-section inline error strip (L159/L191) — deliberately NOT
   * StateBlocks#ErrorBlock: home keeps the mock's compact message + Retry
   * strip so a failed rail sits inline beside a serving one, rather than the
   * whole-page SIGNAL DROPPED card.
   *
   * @param {string} message
   * @param {() => void} onRetry
   * @returns {HTMLElement}
   */
  function SectionError(message, onRetry) {
    if (announce !== undefined) announce(message);
    const strip = el('div', { className: 'sm-error-strip', attrs: { role: 'status' } });
    append(
      strip,
      el('span', { className: 'sm-error-strip__msg', text: message }),
      el('button', {
        className: 'sm-btn sm-btn--retry',
        text: 'Retry',
        testId: TESTIDS.RETRY_BUTTON,
        attrs: { type: 'button' },
        on: { click: () => onRetry() },
      }),
    );
    return strip;
  }

  /**
   * The mock's dashed inline empty strip (L162/L194).
   *
   * @param {string} message
   * @param {{ syncBridge?: boolean }} [opts]
   *   `syncBridge` appends the ADMIN-ONLY "View sync status →" link — the
   *   caller has already applied the role gate, so this stays a dumb renderer.
   * @returns {HTMLElement}
   */
  function SectionEmpty(message, opts = {}) {
    if (announce !== undefined) announce(message);
    const strip = el('div', {
      className: 'sm-empty sm-empty--inline',
      attrs: { role: 'status' },
    });
    append(strip, el('span', { text: message }));
    if (opts.syncBridge === true) {
      append(
        strip,
        ' ',
        el('a', {
          text: HOME_SYNC_BRIDGE_LABEL,
          // Derived from THIS link's accessible name — never the admin-nav's
          // `sync-status-link` (see the module header).
          testId: linkTestId('View sync status'),
          attrs: { href: '#/admin/sync' },
        }),
      );
    }
    return strip;
  }

  /* ── Recently Played albums — the hero cover rail (AC 14.2) ───────────── */

  /** ♥ set read at paint time — home has no live favorites interplay. */
  function favoriteAlbumIds() {
    return new Set(getState === undefined ? [] : getState().catalog.favorites.albums);
  }

  /**
   * Adapt the `/api/home` recently-played section (album + playlist contexts)
   * into the album-only, cover-card shape the hero rail renders: keep the
   * section status, project album entries to their `AlbumCard`, and drop
   * playlist contexts (a cover rail shows albums). An all-playlist page
   * therefore reads as `empty` — the genuine "nothing to show here" state.
   *
   * @param {import('@streamymusic/shared').HomeResponse['recentlyPlayed']} section
   * @returns {{ status: 'ok', items: import('@streamymusic/shared').AlbumCard[] }
   *   | { status: 'empty' } | { status: 'error', code: string }}
   */
  function recentlyPlayedAlbums(section) {
    if (section.status !== 'ok') return section;
    const albums = section.items
      .filter((entry) => entry.contextType === 'album')
      .map((entry) => entry.album);
    return albums.length === 0 ? { status: 'empty' } : { status: 'ok', items: albums };
  }

  /**
   * @param {{ status: 'ok', items: import('@streamymusic/shared').AlbumCard[] }
   *   | { status: 'empty' } | { status: 'error', code: string }} result
   */
  function applyTopRail(result) {
    if (result.status === 'error') {
      raBody.replaceChildren(
        SectionError(HOME_RECENTLY_PLAYED_ERROR_MESSAGE, () => {
          void loadSection();
        }),
      );
      return;
    }
    if (result.status === 'empty') {
      raBody.replaceChildren(SectionEmpty(HOME_RECENTLY_PLAYED_EMPTY_MESSAGE));
      return;
    }

    const favorites = favoriteAlbumIds();
    const rail = el('div', { className: 'sm-rail' });
    for (const album of result.items) {
      append(
        rail,
        AlbumGridCard({
          album,
          // The rail is a server-shaped listing like browse: gone ALBUMS are
          // retained with the ⊘ ribbon (AC 3.9 / 14.2); a gone TRACK could
          // never legitimately appear here, and the shared decision function
          // reports that through `warn` rather than swallowing it.
          surface: 'browse',
          favorite: favorites.has(album.id),
          offline,
          downloaded: downloads.has(album.id),
          onMenu: (item) => handlers.onAlbumMenu(item),
          onContractViolation: warn,
        }),
      );
    }
    raBody.replaceChildren(rail);
  }

  /* ── ON REPEAT — recently played TRACKS (`GET /api/recently-played-tracks`) ─ */

  /**
   * The rail is a HISTORY surface: dto.ts pins that a removed track KEEPS its
   * place here, flagged (AC 3.9 retain rule) — so the shared decision function
   * gets the retain-side `history` surface, never browse's silent exclusion.
   * Offline, the track leans on its ALBUM's roll-up like the FavoritesView
   * rows do — home is handed no per-track download set.
   *
   * @param {import('@streamymusic/shared').TrackCard} track
   * @returns {import('../../components/AvailabilityBadge.js').AvailabilityState|null}
   */
  function stateOfTrack(track) {
    return availabilityState({
      available: track.available,
      formatSupported: track.formatSupported,
      offline,
      downloaded: downloads.has(track.albumId),
      kind: 'track',
      surface: 'history',
      onScopeViolation: (message) =>
        warn(message, { surface: 'home-on-repeat', trackId: track.id }),
    });
  }

  /**
   * One ON REPEAT row: a full-row play BUTTON (the FavoritesView song-row
   * idiom — aria-label "Play <title>", testid `play-<slug>-button`), dressed
   * like the sibling played rows (thumb + two text lines + trailing tags),
   * with the stats rail's yellow play count riding along. A blocked row
   * refuses with the §10.6 per-state copy instead of starting a stream that
   * cannot work.
   *
   * @param {import('@streamymusic/shared').RecentlyPlayedTrackEntry} entry
   * @param {import('@streamymusic/shared').TrackCard[]} playable
   * @returns {HTMLElement}
   */
  function TrackPlayedRow(entry, playable) {
    const track = entry.track;
    const marker = stateOfTrack(track);

    const row = el('button', {
      className: ['sm-row', 'sm-row--shadow', 'sm-row--button'],
      testId: playTrackButton(track.title),
      attrs: { type: 'button', 'aria-label': `Play ${track.title}` },
      styleProps: { opacity: String(availabilityOpacity(marker)) },
      on: {
        click: () => {
          if (marker !== null) {
            handlers.onBlocked(AVAILABILITY_COPY[marker].banner, {
              state: marker,
              trackId: track.id,
            });
            return;
          }
          handlers.onPlayTrack(track, playable);
        },
      },
    });

    const textCell = el('span', { className: 'sm-flex-fill' });
    append(
      textCell,
      el('span', { className: 'sm-line-primary', text: track.title }),
      el('span', {
        className: 'sm-line-secondary',
        text: `${track.artistName} · ${track.albumTitle}`,
      }),
      marker === null ? null : AvailabilityBadge({ state: marker }),
    );

    append(
      row,
      albumThumb({ id: track.albumId, title: track.albumTitle, coverRef: track.coverRef }),
      textCell,
      el('span', { className: 'sm-plays', text: playsLabel(entry.playCount) }),
    );
    return row;
  }

  /** @param {import('@streamymusic/shared').RecentlyPlayedTrackEntry[]} items */
  function applyOnRepeat(items) {
    if (items.length === 0) {
      // An empty `items` is the genuine "no qualifying plays yet" answer
      // (dto.ts), never an error — same inline strip as the sibling rails.
      onRepeatBody.replaceChildren(SectionEmpty(HOME_ON_REPEAT_EMPTY_MESSAGE));
      return;
    }

    const playable = items
      .map((entry) => entry.track)
      .filter((track) => isPlayable(stateOfTrack(track)));
    const list = el('div', { className: 'sm-stack-10' });
    for (const entry of items) append(list, TrackPlayedRow(entry, playable));
    onRepeatBody.replaceChildren(list);
  }

  /* ── loads ────────────────────────────────────────────────────────────── */

  function paintRaLoading() {
    raBody.replaceChildren(
      SkeletonList({ count: 4, variant: 'cover', layout: 'rail', label: 'Loading recently played' }),
    );
  }

  function paintOnRepeatLoading() {
    onRepeatBody.replaceChildren(
      SkeletonList({ count: 4, variant: 'row', label: 'Loading recently played tracks' }),
    );
  }

  /**
   * The ON REPEAT rail's OWN loader. It fetches a DIFFERENT endpoint than the
   * two `getHome` sections, but it keeps their isolation rule (AC 14.3): its
   * failure paints ITS strip with ITS Retry and touches nothing else, under
   * its own generation guard.
   */
  async function loadOnRepeat() {
    const own = (onRepeatGeneration += 1);
    paintOnRepeatLoading();

    try {
      const response = await api.getRecentlyPlayedTracks();
      if (disposed || own !== onRepeatGeneration) return;
      applyOnRepeat(response.items);
    } catch {
      if (disposed || own !== onRepeatGeneration) return;
      onRepeatBody.replaceChildren(
        SectionError(HOME_ON_REPEAT_ERROR_MESSAGE, () => {
          void loadOnRepeat();
        }),
      );
    }
  }

  /**
   * The hero rail's loader: one `/api/home` request, its recently-played albums
   * projected into the cover rail. ON REPEAT loads separately (loadOnRepeat) and
   * the two stay isolated (AC 14.3) — either failing paints only its own strip.
   */
  async function loadSection() {
    const own = (raGeneration += 1);
    paintRaLoading();

    try {
      const home = await api.getHome();
      if (disposed || own !== raGeneration) return;
      applyTopRail(recentlyPlayedAlbums(home.recentlyPlayed));
    } catch {
      if (disposed || own !== raGeneration) return;
      applyTopRail({ status: 'error', code: 'INTERNAL' });
    }
  }

  void loadSection();
  void loadOnRepeat();

  return {
    element: section,
    dispose() {
      // The next settle sees `disposed` and writes nothing into a section
      // the shell has already replaced.
      disposed = true;
    },
  };
}
